import Icon, { Icons } from '@/presentation/components/elements/Icon';
import { useFormatter, useTranslations } from 'use-intl';
import PopularItems from '../../modules/PopularItems';
import InfiniteScroll from '@/components/modules/InfiniteScroll';
import { Children, ReactNode, useState } from 'react';
import Button from '@/components/elements/Button';
import FormattedEther from '@/components/elements/FormattedEther';
import { Address, parseEther } from 'viem';
import { useTransition, animated } from '@react-spring/web';
import { TokenIds, Vault, getTotalTokenIds } from 'nftx.js';
import { QueryStatus } from '@tanstack/react-query';
import ConnectButton from '@/presentation/components/widgets/ConnectButton';
import { getCollectionPath } from '@/presentation/utils/paths';
import FilterToggle from '@/presentation/components/modules/filters/FilterToggle';

export interface NftsProps {
  chainSlug: string;
  hasFilters: boolean;
  currentRefinements: ReactNode;
  vault?: Vault;
  showNfts: boolean;
  onBackClick: () => void;
  onMoreClick: () => void;
  popularCollections: ReactNode;
  hasMoreNfts: boolean;
  hasMoreCollections: boolean;
  nfts: ReactNode;
  status: QueryStatus;
  collections: ReactNode;
  totalNfts: number;
  token: Address | '';
  onConfirmTokenIds: () => void;
  selectedItems: TokenIds;
  priceLoading: boolean;
  totalPrice: `${number}`;
  currencySymbol: string;
  canFilter: boolean;
  filters: ReactNode;
  isConnected: boolean;
}

export const Nfts = ({
  chainSlug,
  vault,
  showNfts,
  onBackClick,
  onMoreClick,
  popularCollections,
  hasMoreNfts,
  hasMoreCollections,
  nfts,
  status,
  collections,
  totalNfts,
  token,
  onConfirmTokenIds,
  selectedItems,
  priceLoading,
  totalPrice,
  currencySymbol,
  hasFilters,
  currentRefinements,
  canFilter,
  filters,
  isConnected,
}: NftsProps) => {
  const [showFilters, setShowFilters] = useState(false);
  const { number } = useFormatter();
  const t = useTranslations('Swap');

  const transitions = useTransition(showNfts, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
  });

  return (
    <>
      {transitions(
        (style, show) =>
          show && (
            <animated.div
              key="nfts"
              style={style}
              className="h-full max-h-full flex flex-col"
            >
              <header>
                <div className="flex justify-between items-center p-4">
                  {showFilters ? (
                    <span className="text-gray-500 font-medium text-sm">
                      {t('Filters.results', { count: totalNfts })}
                    </span>
                  ) : (
                    <button
                      onClick={onBackClick}
                      className="flex items-center text-gray-400 text-sm -ml-1"
                    >
                      <Icon
                        name={Icons.CHEVRON_DOWN}
                        className="transform rotate-90 text-gray-400 mr-1"
                        size={16}
                      />
                      {t('Search.back')}
                    </button>
                  )}
                  <FilterToggle
                    canFilter={canFilter}
                    hasFilters={hasFilters}
                    showFilters={showFilters}
                    onClick={() => setShowFilters(!showFilters)}
                  />
                </div>
              </header>
              {!showFilters && <div className="px-4">{currentRefinements}</div>}
              <section className="overflow-auto hide-scroll">
                {canFilter && vault && (
                  <div className={showFilters ? 'block' : 'hidden'}>
                    {filters}
                  </div>
                )}

                <div
                  className={`${
                    showFilters ? 'hidden' : 'grid'
                  } px-4 grid-cols-3 md:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6 -mx-1 pb-2 max-[70%]`}
                >
                  <InfiniteScroll
                    key="nfts-infinite-scroll"
                    scrollable={hasMoreNfts}
                    onLoadMore={onMoreClick}
                    loaderClassName="col-span-3 md:col-span-4 2xl:col-span-5 3xl:col-span-6"
                  >
                    {status === 'loading' ? (
                      new Array(18)
                        .fill(null)
                        .map((x, i) => (
                          <div
                            className="aspect-square bg-gray-300/50 animate-pulse animate m-1"
                            key={i}
                          />
                        ))
                    ) : Children.count(nfts) ? (
                      nfts
                    ) : isConnected ? (
                      <div className="px-4 py-8 text-center text-gray-500">
                        {'Nothing to see here :('}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center p-4 justify-center">
                        <p className="mb-6">
                          {'Connect your wallet to view your NFTs'}
                        </p>
                        <ConnectButton />
                      </div>
                    )}
                  </InfiniteScroll>
                </div>
              </section>
              <div className="bg-gray-100 mt-auto">
                {showFilters ? (
                  <aside className="p-4 border-t border-gray-300 ">
                    <Button
                      className="w-full"
                      onClick={() => setShowFilters(false)}
                    >
                      {t('Filters.okay')}
                    </Button>
                  </aside>
                ) : (
                  <aside className="p-4 border-t border-gray-300 text-xs text-gray-500">
                    <div className="flex justify-between mb-3">
                      <span>
                        {t('Search.Collection.visible', {
                          max: totalNfts,
                          visible: Children.count(nfts),
                        })}
                      </span>
                      <a
                        href={getCollectionPath({
                          chain: chainSlug,
                          collectionSlug: vault?.collectionSlug ?? token,
                          vaultSlug: vault?.slug,
                        })}
                        className="inline-flex justify-end items-center hover:underline"
                      >
                        <span className="mr-1">
                          {t('Search.Collection.vault')}
                        </span>
                        <Icon
                          name={Icons.ARROW_DOWN}
                          size={16}
                          className="transform -rotate-90"
                        />
                      </a>
                    </div>
                    <Button
                      className="w-full"
                      onClick={onConfirmTokenIds}
                      variant={!selectedItems.length ? 'tertiary' : 'primary'}
                    >
                      {t('Search.Collection.proceed', {
                        selected: getTotalTokenIds(selectedItems),
                      })}
                    </Button>
                  </aside>
                )}

                <footer className="bg-gray-200 border-t border-gray-300 px-4 py-3 text-gray-500 text-xs">
                  <div className="flex justify-between">
                    <span>
                      {t.rich('Search.selected.text', {
                        count: getTotalTokenIds(selectedItems),
                        average: priceLoading ? (
                          <Icon
                            name={Icons.SPINNER_GAP}
                            className="animate animate-spin inline-flex"
                            size={12}
                            key="spinner"
                          />
                        ) : (
                          t('Search.selected.average', {
                            symbol: currencySymbol,
                            average: number(
                              Number(totalPrice) /
                                getTotalTokenIds(selectedItems)
                            ),
                          })
                        ),
                      } as any)}
                    </span>
                    <span>
                      {t.rich('Search.price', {
                        symbol: currencySymbol,
                        price: priceLoading ? (
                          <Icon
                            name={Icons.SPINNER_GAP}
                            className="animate animate-spin inline-flex"
                            size={12}
                            key="spinner"
                          />
                        ) : (
                          <FormattedEther
                            value={parseEther(totalPrice)}
                            key="price"
                          />
                        ),
                      } as any)}
                    </span>
                  </div>
                </footer>
              </div>
            </animated.div>
          )
      )}
      {transitions(
        (style, show) =>
          !show && (
            <animated.div
              key="collections"
              style={style}
              className="h-full max-h-full flex flex-col"
            >
              <div className="px-4 pt-2">
                <h3 className="text-gray-400 font-semibold text-xs uppercase my-2">
                  {t('Search.popular')}
                </h3>
                <PopularItems items={popularCollections} />
              </div>

              <section>
                <h3 className="text-gray-400 font-semibold text-xs uppercase mx-4 my-2">
                  {t('Search.all')}
                </h3>
                <ul>
                  <InfiniteScroll
                    key="collections-infinite-scroll"
                    loaderElement="li"
                    scrollable={hasMoreCollections}
                    onLoadMore={onMoreClick}
                  >
                    {status === 'loading' ? (
                      new Array(5).fill(null).map((x, i) => (
                        <div
                          className="w-full flex items-center px-4 py-2 animate animate-pulse"
                          key={i}
                        >
                          <div className="w-8 h-8 flex-none mr-2 rounded-full bg-gray-300/50" />
                          <div className="h-6 w-1/4 bg-gray-300/50 rounded-sm" />
                          <div className="ml-auto h-4 w-1/3 bg-gray-300/50 rounded-sm" />
                        </div>
                      ))
                    ) : Children.count(collections) ? (
                      collections
                    ) : isConnected ? (
                      <div className="px-4 py-8 text-center text-gray-500">
                        {'Nothing to see here :('}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center p-4 justify-center">
                        <p className="mb-6">
                          {'Connect your wallet to view your NFTs'}
                        </p>
                        <ConnectButton />
                      </div>
                    )}
                  </InfiniteScroll>
                </ul>
              </section>
            </animated.div>
          )
      )}
    </>
  );
};

export default Nfts;
